import type { TranslateParams } from '@core/i18n/i18n.models';
import type { TranslationKey } from '@core/i18n/translations/en';
import { formatRelativeTime } from '@core/dates/date.utils';
import { NOTIFICATION_TEXT } from './notifications.constants';
import {
  CUSTOM_NOTIFICATION_TYPE,
  isNotificationMessage,
  type AppNotification,
  type NotificationDynamicFields,
} from './notifications.models';

const API_VERSION_MARKER = '/api/v1/';

type Translate = (key: TranslationKey, params?: TranslateParams) => string;

export function sortNotifications(data: readonly AppNotification[]): {
  readonly notifications: readonly AppNotification[];
  readonly newNotificationCount: number;
} {
  const unread: AppNotification[] = [];
  const read: AppNotification[] = [];

  for (const notification of data) {
    if (notification.status === 'new') {
      unread.push(notification);
    } else {
      read.push(notification);
    }
  }

  return {
    notifications: [...unread, ...read],
    newNotificationCount: unread.length,
  };
}

export function translateNotification(
  notification: AppNotification,
  t: Translate,
): AppNotification {
  if (notification.type === CUSTOM_NOTIFICATION_TYPE) {
    return notification;
  }

  const params = toTranslateParams(notification.dynamicFields);

  return {
    ...notification,
    title: translateText(notification.title, params, t),
    message: notification.message ? translateText(notification.message, params, t) : '',
  };
}

export function toApiPath(url: string, apiUrl = ''): string {
  const base = apiUrl.replace(/\/$/, '');

  if (base && (url === base || url.startsWith(`${base}/`))) {
    const rest = url.slice(base.length);

    return rest.startsWith('/') ? rest : `/${rest}`;
  }

  const index = url.indexOf(API_VERSION_MARKER);

  if (index >= 0) {
    return `/${url.slice(index + API_VERSION_MARKER.length)}`;
  }

  return url.startsWith('/') ? url : `/${url}`;
}

export function formatRelative(iso: string, language?: string): string {
  return formatRelativeTime(iso, language);
}

function translateText(value: string, params: TranslateParams | undefined, t: Translate): string {
  if (!isNotificationMessage(value)) {
    return value;
  }

  return t(NOTIFICATION_TEXT[value], params);
}

function toTranslateParams(fields: NotificationDynamicFields | null): TranslateParams | undefined {
  if (!fields) {
    return undefined;
  }

  return {
    invited_by: fields.invitedBy,
    workspace_name: fields.workspaceName,
    action_by: fields.actionBy,
    date: fields.date,
    user_affected: fields.userAffected,
  };
}
